import { formatJobMetadata } from 'constants/roleLevel';
import { useDuplicateJobRoleModalFindOneQuery } from 'generated/graphql';
import { FC } from 'react';
import { Loader, TextInputField } from '@spotted-zebra-uk/ui-components';
import { TDuplicableJobRole } from './DuplicateJobRoleModal.helpers';
import styles from './DuplicateJobRoleModal.module.scss';

type TDuplicateJobRolePreviewProps = {
  jobRole: TDuplicableJobRole;
  name: string;
  onNameChange: (value: string) => void;
  qualifier: string;
  onQualifierChange: (value: string) => void;
};

// Order soft-skill categories the way the design presents them; unknown
// categories fall to the end.
const CATEGORY_PRIORITY: Record<string, number> = {
  RIGHT_FOR_THE_ROLE: 0,
  RIGHT_FOR_US: 1,
  RIGHT_FOR_THE_FUTURE: 2,
  COGNITIVE_SUMMARY: 3,
};

type TSkillRow = { id: number; name: string; weight: number };
type TSkillGroup = { category: string; label: string; skills: TSkillRow[] };

const DuplicateJobRolePreview: FC<TDuplicateJobRolePreviewProps> = ({
  jobRole,
  name,
  onNameChange,
  qualifier,
  onQualifierChange,
}) => {
  // 'no-cache' returns the raw network response instead of writing it to the
  // normalized cache and reading it back. The read-back can be incomplete (e.g.
  // `technicalSkill` is selected without its `subId` keyField), which leaves the
  // query stuck on `loading: true` even after the response arrives.
  const { data, loading } = useDuplicateJobRoleModalFindOneQuery({
    variables: { id: jobRole.id },
    fetchPolicy: 'no-cache',
  });

  const softSkills =
    data?.JobRoleFindOne?.skillsProfiles?.[0]?.successProfileSoftSkills ?? [];

  const groups: TSkillGroup[] = Object.values(
    softSkills.reduce<Record<string, TSkillGroup>>((acc, skill) => {
      const category = skill.softSkill.category;
      if (!acc[category]) {
        acc[category] = {
          category,
          label: formatJobMetadata(category),
          skills: [],
        };
      }
      acc[category].skills.push({
        id: skill.id,
        name: skill.name,
        weight: skill.weight,
      });
      return acc;
    }, {}),
  ).sort(
    (a, b) =>
      (CATEGORY_PRIORITY[a.category] ?? 99) -
      (CATEGORY_PRIORITY[b.category] ?? 99),
  );

  // Technical skills come back without a weight, so they're shown as a flat list.
  const technicalSkills =
    data?.JobRoleFindOne?.skillsProfiles?.[0]?.successProfileTechnicalSkills ??
    [];

  // Shown as a compact dot-separated line, mirroring the result cards in step 1.
  const meta = [
    jobRole.levelLabel,
    jobRole.familyLabel,
    jobRole.sourceLabel,
  ].filter(Boolean);

  return (
    <div className={styles.preview} data-testid="duplicate-job-role-preview">
      <div className={styles.nameField}>
        <TextInputField
          id="duplicate-job-role-name"
          label="Duplicated Role Skills Profile name"
          name="name"
          value={name}
          onChange={onNameChange}
        />
        <TextInputField
          id="duplicate-job-role-qualifier"
          label="Additional information"
          name="qualifier"
          value={qualifier}
          onChange={onQualifierChange}
        />
      </div>

      <span className={styles.previewMeta}>
        {meta.map((part, index) => (
          <span key={part}>
            {index > 0 && <span className={styles.resultMetaDot}>•</span>}
            {part}
          </span>
        ))}
      </span>

      <div className={styles.skillsHeader}>
        <span className={styles.skillsHeaderTitle}>Skills</span>
        <span className={styles.skillsHeaderNote}>Weight</span>
      </div>

      {loading && !data ? (
        <div className={styles.skillsLoading}>
          {' '}
          <Loader />
        </div>
      ) : !groups.length && !technicalSkills.length ? (
        <p className={styles.skillsEmpty}>No skills to display.</p>
      ) : (
        <div className={styles.skillGroups}>
          {groups.map((group) => {
            const maxWeight = Math.max(...group.skills.map((s) => s.weight), 1);
            return (
              <div key={group.category} className={styles.skillGroup}>
                <div className={styles.skillGroupHeader}>
                  <span className={styles.skillGroupDot} />
                  <span className={styles.skillGroupLabel}>{group.label}</span>
                  <span className={styles.skillGroupCount}>
                    {group.skills.length}
                  </span>
                </div>
                {group.skills.map((skill) => (
                  <div key={skill.id} className={styles.skillRow}>
                    <span className={styles.skillName}>{skill.name}</span>
                    {skill.weight > 0 && (
                      <span className={styles.skillBar}>
                        <span
                          className={styles.skillBarFill}
                          style={{
                            width: `${Math.round(
                              (skill.weight / maxWeight) * 100,
                            )}%`,
                          }}
                        />
                      </span>
                    )}
                    <span className={styles.skillWeight}>
                      {Math.round(skill.weight * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            );
          })}

          {technicalSkills.length > 0 && (
            <div className={styles.skillGroup}>
              <div className={styles.skillGroupHeader}>
                <span className={styles.skillGroupDot} />
                <span className={styles.skillGroupLabel}>Technical skills</span>
                <span className={styles.skillGroupCount}>
                  {technicalSkills.length}
                </span>
              </div>
              {technicalSkills.map((skill) => (
                <div key={skill.id} className={styles.skillRow}>
                  <span className={styles.skillName}>
                    {skill.technicalSkill?.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DuplicateJobRolePreview;
